import { FormApi } from "final-form";
import React from "react";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import AuthService from "src/services/authService";
import { getEnv } from "src/services/configService";
import { withRequiredRole } from "src/ui/navigation/utils/withRequiredRole";

import {
    attachLicense,
    deleteLicenses,
    detachLicense,
    grantLicenses,
    inspectLicense,
    LicenseInspection,
    moveLicense,
    reviveLicense,
    revokeLicense,
} from "@admin/api/license";
import { useGetLicenseStatistics } from "@admin/hooks/useGetLicenseStatistics";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Divider } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useStyles } from "./AdminLicensesPage.style";
import {
    AttachFormValues,
    attachSchema,
    DeleteFormValues,
    deleteSchema,
    DetachFormValues,
    detachSchema,
    GrantFormValues,
    grantSchema,
    InspectFormValues,
    inspectSchema,
    MoveFormValues,
    moveSchema,
    ReviveFormValues,
    reviveSchema,
    RevokeFormValues,
    revokeSchema,
} from "./AdminLicensesPage.validation";
import { InspectionResult } from "./InspectionResult";
import { ActionHeading, LicenseActionForm, LicenseActionStrip, licenseFieldWidth } from "./LicenseActionStrip";
import { LicenseStatistics } from "./LicenseStatistics";

const AdminLicensesPage = () => {
    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useStyles();
    const [inspection, setInspection] = React.useState<LicenseInspection | null>(null);

    const { data: statistics, isLoading, isError, refetch: refetchStatistics } = useGetLicenseStatistics();

    const isAdmin = AuthService.isUserAdmin();
    const canInspect = isAdmin && getEnv() === "development";

    const licenseKeyField = {
        name: "licenseKey",
        placeholder: t("pages:adminLicensesPage.input.licenseKey"),
        width: licenseFieldWidth.licenseKey,
    };

    const onGrant = async (values: GrantFormValues, form: FormApi<GrantFormValues>) => {
        const userEmail = values.userEmail.trim();
        const grantCount = Number(values.grantCount);

        try {
            await grantLicenses({ userEmail, groupId: Number(values.groupId), grantCount });
            showSuccessToast(t("pages:adminLicensesPage.grant.toast.success", { count: grantCount, email: userEmail }));
            form.restart();
            refetchStatistics();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminLicensesPage.grant.toast.error"));
        }
    };

    const onMove = async (values: MoveFormValues, form: FormApi<MoveFormValues>) => {
        try {
            await moveLicense({ licenseKey: values.licenseKey.trim(), groupId: Number(values.groupId) });
            showSuccessToast(t("pages:adminLicensesPage.move.toast.success"));
            form.restart();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminLicensesPage.move.toast.error"));
        }
    };

    const onRevoke = async (values: RevokeFormValues, form: FormApi<RevokeFormValues>) => {
        try {
            await revokeLicense({ licenseKey: values.licenseKey.trim(), reason: values.reason.trim() });
            showSuccessToast(t("pages:adminLicensesPage.revoke.toast.success"));
            form.restart();
            refetchStatistics();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminLicensesPage.revoke.toast.error"));
        }
    };

    const onRevive = async (values: ReviveFormValues, form: FormApi<ReviveFormValues>) => {
        try {
            await reviveLicense({
                licenseKey: values.licenseKey.trim(),
                groupId: Number(values.groupId),
                reason: values.reason.trim(),
            });
            showSuccessToast(t("pages:adminLicensesPage.revive.toast.success"));
            form.restart();
            refetchStatistics();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminLicensesPage.revive.toast.error"));
        }
    };

    const onAttach = async (values: AttachFormValues, form: FormApi<AttachFormValues>) => {
        try {
            await attachLicense({
                licenseKey: values.licenseKey.trim(),
                deviceSerial: values.deviceSerial.trim(),
            });
            showSuccessToast(t("pages:adminLicensesPage.attach.toast.success"));
            form.restart();
            refetchStatistics();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminLicensesPage.attach.toast.error"));
        }
    };

    const onDetach = async (values: DetachFormValues, form: FormApi<DetachFormValues>) => {
        try {
            await detachLicense({ licenseKey: values.licenseKey.trim(), reason: values.reason.trim() });
            showSuccessToast(t("pages:adminLicensesPage.detach.toast.success"));
            form.restart();
            refetchStatistics();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminLicensesPage.detach.toast.error"));
        }
    };

    // the key stays in place so the generated data can be read next to the license it belongs to
    const onInspect = async (values: InspectFormValues) => {
        try {
            setInspection(await inspectLicense({ licenseKey: values.licenseKey.trim() }));
            showSuccessToast(t("pages:adminLicensesPage.inspect.toast.success"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminLicensesPage.inspect.toast.error"));
        }
    };

    const onDelete = async (values: DeleteFormValues, form: FormApi<DeleteFormValues>) => {
        try {
            await deleteLicenses({
                licenseKey: values.licenseKey.trim(),
                reason: values.reason.trim(),
            });
            showSuccessToast(t("pages:adminLicensesPage.delete.toast.success"));
            form.restart();
            refetchStatistics();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminLicensesPage.delete.toast.error"));
        }
    };

    return (
        <PageContainer fullWidth={false}>
            <Box className={classes.pageLayout}>
                <SectionPanel>
                    <LicenseStatistics
                        statistics={statistics}
                        isLoading={isLoading}
                        isError={isError}
                        onRetry={refetchStatistics}
                    />
                    <Divider className={classes.panelRule} />
                    <ActionHeading
                        title={t("pages:adminLicensesPage.grant.title")}
                        description={t("pages:adminLicensesPage.grant.description")}
                        icon="fa-solid fa-key"
                    />
                    <LicenseActionForm<GrantFormValues>
                        title={t("pages:adminLicensesPage.grant.title")}
                        buttonLabel={t("pages:adminLicensesPage.grant.button")}
                        fields={[
                            {
                                name: "userEmail",
                                label: t("pages:adminLicensesPage.input.userEmail"),
                                width: licenseFieldWidth.userEmail,
                            },
                            {
                                name: "groupId",
                                label: t("common:label.groupId"),
                                numeric: true,
                                width: licenseFieldWidth.number,
                            },
                            {
                                name: "grantCount",
                                label: t("pages:adminLicensesPage.input.grantCount"),
                                numeric: true,
                            },
                        ]}
                        schema={grantSchema}
                        initialValues={{ userEmail: "", groupId: "0", grantCount: "0" }}
                        onSubmit={onGrant}
                    />
                </SectionPanel>

                <LicenseActionStrip<MoveFormValues>
                    title={t("pages:adminLicensesPage.move.title")}
                    description={t("pages:adminLicensesPage.move.description")}
                    icon="fa-solid fa-arrow-right-arrow-left"
                    buttonLabel={t("pages:adminLicensesPage.move.button")}
                    fields={[
                        licenseKeyField,
                        {
                            name: "groupId",
                            label: t("pages:adminLicensesPage.input.newGroupId"),
                            numeric: true,
                        },
                    ]}
                    schema={moveSchema}
                    initialValues={{ licenseKey: "", groupId: "0" }}
                    onSubmit={onMove}
                />

                <LicenseActionStrip<ReviveFormValues>
                    title={t("pages:adminLicensesPage.revive.title")}
                    description={t("pages:adminLicensesPage.revive.description")}
                    icon="fa-solid fa-rotate-left"
                    buttonLabel={t("pages:adminLicensesPage.revive.button")}
                    fields={[
                        licenseKeyField,
                        {
                            name: "groupId",
                            label: t("common:label.groupId"),
                            numeric: true,
                            width: licenseFieldWidth.number,
                        },
                        { name: "reason", label: t("pages:adminLicensesPage.input.revivalReason") },
                    ]}
                    schema={reviveSchema}
                    initialValues={{ licenseKey: "", groupId: "0", reason: "" }}
                    onSubmit={onRevive}
                />

                {isAdmin ? (
                    <LicenseActionStrip<AttachFormValues>
                        title={t("pages:adminLicensesPage.attach.title")}
                        description={t("pages:adminLicensesPage.attach.description")}
                        icon="fa-solid fa-link"
                        buttonLabel={t("pages:adminLicensesPage.attach.button")}
                        fields={[
                            licenseKeyField,
                            {
                                name: "deviceSerial",
                                label: t("pages:adminLicensesPage.input.deviceSerial"),
                            },
                        ]}
                        schema={attachSchema}
                        initialValues={{ licenseKey: "", deviceSerial: "" }}
                        onSubmit={onAttach}
                    />
                ) : null}

                {isAdmin ? (
                    <LicenseActionStrip<DetachFormValues>
                        title={t("pages:adminLicensesPage.detach.title")}
                        description={t("pages:adminLicensesPage.detach.description")}
                        icon="fa-solid fa-link-slash"
                        buttonLabel={t("pages:adminLicensesPage.detach.button")}
                        tone="danger"
                        fields={[
                            licenseKeyField,
                            {
                                name: "reason",
                                label: t("pages:adminLicensesPage.input.detachmentReason"),
                            },
                        ]}
                        schema={detachSchema}
                        initialValues={{ licenseKey: "", reason: "" }}
                        onSubmit={onDetach}
                    />
                ) : null}

                <LicenseActionStrip<RevokeFormValues>
                    title={t("common:button.revoke")}
                    description={t("pages:adminLicensesPage.revoke.description")}
                    icon="fa-solid fa-ban"
                    buttonLabel={t("common:button.revoke")}
                    tone="destructive"
                    fields={[licenseKeyField, { name: "reason", label: t("pages:adminLicensesPage.input.revocationReason") }]}
                    schema={revokeSchema}
                    initialValues={{ licenseKey: "", reason: "" }}
                    onSubmit={onRevoke}
                />

                {isAdmin ? (
                    <LicenseActionStrip<DeleteFormValues>
                        title={t("common:button.delete")}
                        description={t("pages:adminLicensesPage.delete.description")}
                        icon="fa-solid fa-trash"
                        buttonLabel={t("common:button.delete")}
                        tone="destructive"
                        fields={[
                            licenseKeyField,
                            {
                                name: "reason",
                                label: t("pages:adminLicensesPage.input.deletionReason"),
                            },
                        ]}
                        schema={deleteSchema}
                        initialValues={{ licenseKey: "", reason: "" }}
                        onSubmit={onDelete}
                    />
                ) : null}

                {canInspect ? (
                    <LicenseActionStrip<InspectFormValues>
                        title={t("pages:adminLicensesPage.inspect.title")}
                        description={t("pages:adminLicensesPage.inspect.description")}
                        icon="fa-solid fa-magnifying-glass"
                        buttonLabel={t("pages:adminLicensesPage.inspect.button")}
                        fields={[licenseKeyField]}
                        schema={inspectSchema}
                        initialValues={{ licenseKey: "" }}
                        onSubmit={onInspect}
                    >
                        {inspection ? <InspectionResult inspection={inspection} /> : null}
                    </LicenseActionStrip>
                ) : null}
            </Box>
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminLicensesPage, {
        title: i18next.t("pages:adminLicensesPage.metaTitle"),
        description: i18next.t("pages:adminLicensesPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole, EmteriaRoles.EmteriaSalesRole],
);
